import { Home, History, HelpCircle, FileText, User, LogOut } from "lucide-react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth-provider";
import { useEffect } from "react";
import { motion } from "framer-motion";

interface SidebarProps {
  onClose: () => void

}


export interface User {
  id: string
  mobile: string
  firstname: string
  lastname: string
  email?: string
  active?: boolean
  created_at?: Date
  updated_at?: Date

}



export default function Sidebar({ onClose }: SidebarProps) {

  const { t } = useTranslation();
  const { user, logout, isAuthenticated } = useAuth()

  function handleLogout() {
    logout() // logout() handles navigation with cache-busting for Safari
  }


  useEffect(() => {
    // Prevent scrolling on the body when the sidebar is open
    document.body.style.overflow = 'hidden';

    return () => {
      // Re-enable scrolling when the sidebar is closed/unmounted
      document.body.style.overflow = 'unset';
    };
  }, []);






  return (
    <div className="fixed inset-0 z-[100] lg:hidden h-[100dvh]">
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sidebar Panel */}
      <motion.div
        initial={{ x: "-100%" }}
        animate={{ x: 0 }}
        exit={{ x: "-100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="absolute left-0 top-0 h-full w-[65vw] max-w-xs bg-white shadow-2xl flex flex-col rounded-r-3xl overflow-y-auto"
      >
        {/* Top: Brand */}
        <div className="flex items-center px-6 py-5 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-3">
            <img src="/images/cheche.svg" alt="CHECHE" width={80} height={18} className="h-5 w-auto" />
            <span className="h-5 w-px bg-gray-300" />
            <img src="/images/qetero_logo_text_3.svg" alt="Qetero" width={80} height={18} className="h-5 w-auto" />
          </div>
        </div>

        {/* User Profile Section */}
        {user && (

          <div className="px-6 py-6 border-b border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-pink-200 ring-4 ring-pink-50">
                {(user?.firstname?.[0] || user?.lastname?.[0] || "?").toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-gray-900 truncate text-lg">{user?.firstname} {user?.lastname}</h3>
                <p className="text-xs text-gray-500 truncate font-medium">{user?.mobile}</p>
                {user?.active && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800 mt-1">
                    Active
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Menu Items */}
        <nav className="pl-2 pr-0 py-6 space-y-1">
          <Link to={`/`} onClick={onClose} className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-gray-600 hover:bg-pink-50 hover:text-pink-700 transition-all duration-200 group">
            <Home className="w-5 h-5 text-gray-400 group-hover:text-pink-500 transition-colors" />
            <span className="text-sm font-bold">{t("home")}</span>
          </Link>
          <Link to={`/booking/history`} onClick={onClose} className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-gray-600 hover:bg-pink-50 hover:text-pink-700 transition-all duration-200 group">
            <History className="w-5 h-5 text-gray-400 group-hover:text-pink-500 transition-colors" />
            <span className="text-sm font-bold">{t("bookingHistory")}</span>
          </Link>
          <Link to="/help" onClick={onClose} className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-gray-600 hover:bg-pink-50 hover:text-pink-700 transition-all duration-200 group">
            <HelpCircle className="w-5 h-5 text-gray-400 group-hover:text-pink-500 transition-colors" />
            <span className="text-sm font-bold">{t("help")}</span>
          </Link>
          <Link to={`/terms-and-conditions`} onClick={onClose} className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-gray-600 hover:bg-pink-50 hover:text-pink-700 transition-all duration-200 group ">
            <FileText className="w-5 h-5 text-gray-400 group-hover:text-pink-500 transition-colors" />
            <span className="text-sm font-bold">{t("termsAndConditions")}</span>
          </Link>
          <Link to={`/profile`} onClick={onClose} className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-gray-600 hover:bg-pink-50 hover:text-pink-700 transition-all duration-200 group">
            <User className="w-5 h-5 text-gray-400 group-hover:text-pink-500 transition-colors" />
            <span className="text-sm font-bold">{t("profile")}</span>
          </Link>
        </nav>

        {/* Signout Button */}
        {isAuthenticated && (
          <div className="p-6 border-t border-gray-100 bg-gray-50/50 mt-auto">
            <button
              className="w-full flex items-center justify-center gap-2 text-white font-bold py-3.5 bg-pink-600 rounded-xl shadow-lg shadow-pink-200 hover:bg-pink-700 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 active:scale-95"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">{t("logout")}</span>
            </button>
          </div>
        )}
      </motion.div>
    </div>
  )
}
